import React, { useState } from 'react';
import { Mail, Phone, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useTranslation, Trans } from 'react-i18next';

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('');

    const formData = {
      name: e.target.name.value,
      email: e.target.email.value,
      message: e.target.message.value,
    };

    try {
      const response = await fetch(process.env.REACT_APP_GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      // Since Apps Script returns no-cors, assume success if no error
      setStatus('success');
      e.target.reset();
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-[#0A1F44] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-4">{t('contact.header.title')}</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {t('contact.header.description')}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-12">
        {/* Contact Information */}
        <div>
          <h2 className="text-3xl font-bold text-[#0A1F44] mb-6">{t('contact.info.title')}</h2>
          <p className="text-gray-700 mb-8 leading-relaxed">
            {t('contact.info.description')}
          </p>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <Mail className="h-6 w-6 text-[#FF6A00]" />
              <div>
                <p className="font-semibold text-[#0A1F44]">{t('contact.info.email')}</p>
                <a
                  href="mailto:support@hh-enterprise.net"
                  className="text-[#FF6A00] hover:underline"
                >
                  support@hh-enterprise.net
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Phone className="h-6 w-6 text-[#FF6A00]" />
              <div>
                <p className="font-semibold text-[#0A1F44]">{t('contact.info.phone')}</p>
                <p className="text-gray-700">+65 8306 4033</p>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <p className="text-gray-600">
              <Trans i18nKey="contact.info.cert" components={{ strong: <strong />, br: <br /> }} />
            </p>
          </div>
        </div>

        {/* Contact Form */}
        <Card className="border-none shadow-lg">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-[#0A1F44] mb-6">
              {t('contact.form.title')}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('contact.form.name')}
                </label>
                <input
                  type="text"
                  name="name"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#FF6A00] focus:outline-none"
                  placeholder={t('contact.form.placeholderName')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('contact.form.email')}
                </label>
                <input
                  type="email"
                  name="email"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#FF6A00] focus:outline-none"
                  placeholder={t('contact.form.placeholderEmail')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('contact.form.message')}
                </label>
                <textarea
                  name="message"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 h-32 focus:ring-2 focus:ring-[#FF6A00] focus:outline-none resize-none"
                  placeholder={t('contact.form.placeholderMessage')}
                  required
                ></textarea>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className={`w-full flex items-center justify-center gap-2 text-white ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[#FF6A00] hover:bg-[#E55F00]'
                }`}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      ></path>
                    </svg>
                    {t('contact.form.sending')}
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    {t('contact.form.submit')}
                  </>
                )}
              </Button>

              {status === 'success' && (
                <p className="text-green-600 text-sm mt-2">
                  {t('contact.form.success')}
                </p>
              )}
              {status === 'error' && (
                <p className="text-red-600 text-sm mt-2">
                  {t('contact.form.error')}
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Footer CTA */}
      <section className="bg-[#0A1F44] text-white py-12">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-3xl font-bold mb-4">
            {t('contact.footer.title')}
          </h2>
          <p className="text-gray-300 mb-6">
            {t('contact.footer.description')}
          </p>
          <a href="mailto:support@hh-enterprise.net">
            <Button
              size="lg"
              className="bg-[#FF6A00] hover:bg-[#E55F00] text-white"
            >
              {t('contact.footer.button')}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
